import { CLIENTS } from "@/lib/siteData";

/**
 * Pure-CSS infinite marquee. Duplicates each row so the translate(-50%)
 * end-state aligns with the start of the second copy = seamless loop.
 *
 * With 83 logos we split into 3 rows (~28 logos each), running at
 * different speeds/directions for a premium, layered feel.
 */
const LogoPill = ({ client, idx }) => {
  if (client.logo) {
    return (
      <div
        data-testid={`client-logo-${idx}`}
        className="flex-shrink-0 flex items-center justify-center h-20 w-[170px] rounded-xl border border-[#E3A21A]/10 bg-[#1E2A1E] grayscale hover:grayscale-0 hover:border-[#E3A21A]/40 hover:bg-[#121A12] transition-all duration-300"
        title={client.name}
      >
        <img
          src={client.logo}
          alt={client.name}
          loading="lazy"
          className="max-h-14 max-w-[130px] object-contain"
        />
      </div>
    );
  }
  return (
    <div
      data-testid={`client-pill-${idx}`}
      className="flex-shrink-0 flex items-center justify-center h-20 px-8 min-w-[200px] rounded-xl border border-[#E3A21A]/15 bg-[#1E2A1E] hover:border-[#E3A21A]/45 transition-all duration-300"
    >
      <span className="font-display text-lg tracking-tight text-[#F5F5F0] whitespace-nowrap">
        {client.name}
      </span>
    </div>
  );
};

const MarqueeRow = ({ items, direction = "left", speed = "30s", offset = 0 }) => {
  // Duplicate to enable seamless infinite loop
  const doubled = [...items, ...items];
  const animClass = direction === "left" ? "marquee-track" : "marquee-track-reverse";
  return (
    <div className="overflow-hidden">
      <div
        className={`${animClass} flex gap-5 w-max py-3`}
        style={{ animationDuration: speed }}
      >
        {doubled.map((c, i) => (
          <LogoPill key={`${direction}-${i}`} client={c} idx={i + offset} />
        ))}
      </div>
    </div>
  );
};

export const ClientsMarquee = () => {
  const total = CLIENTS.length;
  // Split into 3 roughly equal rows
  const third = Math.ceil(total / 3);
  const rowA = CLIENTS.slice(0, third);
  const rowB = CLIENTS.slice(third, third * 2);
  const rowC = CLIENTS.slice(third * 2);

  return (
    <section
      id="clients"
      data-testid="clients-marquee"
      className="relative py-24 sm:py-28 overflow-hidden"
    >
      <div className="max-w-7xl mx-auto px-6 sm:px-8 md:px-12">
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-12">
          <div className="max-w-2xl">
            <div className="text-xs tracking-[0.25em] uppercase font-bold text-[#E3A21A] mb-4">
              Trusted By
            </div>
            <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl tracking-tight text-[#F5F5F0] font-medium">
              <span className="text-[#E3A21A] font-semibold">{total}+</span>{" "}
              brands that chose{" "}
              <span className="text-[#E3A21A] italic font-light">
                revenue over noise.
              </span>
            </h2>
          </div>
          <p className="max-w-sm text-[#A8B8A8] text-sm leading-relaxed">
            Real estate, lifestyle and education brands across India — scaling
            with Digibis Digital.
          </p>
        </div>
      </div>

      {/* Three marquee rows, alternating direction */}
      <div className="relative space-y-5">
        {/* Edge fades */}
        <div className="pointer-events-none absolute left-0 top-0 bottom-0 w-24 md:w-52 bg-gradient-to-r from-[#121A12] to-transparent z-10" />
        <div className="pointer-events-none absolute right-0 top-0 bottom-0 w-24 md:w-52 bg-gradient-to-l from-[#121A12] to-transparent z-10" />

        <MarqueeRow items={rowA} direction="left" speed="60s" offset={0} />
        <MarqueeRow items={rowB} direction="right" speed="75s" offset={200} />
        <MarqueeRow items={rowC} direction="left" speed="90s" offset={400} />
      </div>
    </section>
  );
};
